/* eslint-disable import/no-cycle */
import { ActionTypes as candidateActionTypes } from './candidate';
import { ActionTypes as electionActionTypes } from './election';
import { election as electionService, race as raceService } from '../../services';

export const ActionTypes = {
  SET_RACES: 'SET_RACES',
  SET_ALL_APPROVED_RACES: 'SET_ALL_APPROVED_RACES',
  SET_ALL_UNAPPROVED_RACES: 'SET_ALL_UNAPPROVED_RACES',
  CLEAR_RACES: 'CLEAR_RACES',
  SET_SELECTED_RACE: 'SET_SELECTED_RACE',
  SET_SELECTED_RACE_OBJ: 'SET_SELECTED_RACE_OBJ',
  CLEAR_SELECTED_RACE: 'CLEAR_SELECTED_RACE',
  API_ERROR: 'API_ERROR',
};

/**
 * @description action creator for selecting a race
 * @param {String} raceId the id of the race to select
 */
export const setSelectedRace = (raceId) => {
  return (dispatch, getState) => {
    dispatch({ type: ActionTypes.SET_SELECTED_RACE, payload: raceId });

    // set the candidates if possible
    const selectedRace = getState().races.races[raceId];

    dispatch({
      type: selectedRace ? candidateActionTypes.SET_CANDIDATES : candidateActionTypes.CLEAR_CANDIDATES,
      payload: selectedRace ? selectedRace.candidates : [],
    });
  };
};

/**
 * @description action creator for clearing selected race
 */
export const clearSelectedRace = () => {
  return (dispatch) => {
    dispatch({ type: ActionTypes.CLEAR_SELECTED_RACE });
    dispatch({ type: candidateActionTypes.CLEAR_CANDIDATES });
  };
};

/**
 * @description action creator for selecting a race
 * @param {String} race the id of the race to select
 */
export const fetchRace = (raceId) => {
  return async (dispatch) => {
    try {
      // grab the race and its corresponding election
      const elections = await raceService.fetchElectionsForRace(raceId);
      const race = await raceService.fetchRaceById(raceId);

      // store election and set first one (should be only)
      dispatch({ type: electionActionTypes.SET_ELECTIONS, payload: elections });
      dispatch({ type: electionActionTypes.SET_SELECTED_ELECTION_OBJ, payload: elections[0] });

      // store race and select this one
      dispatch({ type: ActionTypes.SET_RACES, payload: elections[0] ? elections[0].races : [race] });
      dispatch({ type: ActionTypes.SET_SELECTED_RACE_OBJ, payload: race });

      // store this race's candidates
      dispatch({ type: candidateActionTypes.SET_CANDIDATES, payload: race.candidates });
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_RACES,
          error,
        },
      });
    }
  };
};

/**
 * @description action creator for creating a race
 * @param {Object} fields fields about election to create
 * @param {String} electionId id of election to add race to
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const createRace = (fields, electionId, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      const result = await raceService.createRace(fields);
      await electionService.addRaceToElection(electionId, result[0]._id);

      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'CREATE_RACE',
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for selecting a race
 * @param {String} race the id of the race to select
 */
export const setRaceTopicMeaning = (raceId, topic, meaning) => {
  return async (dispatch) => {
    try {
      // update race topic meaning
      await raceService.setRaceTopicMeaning(raceId, topic, meaning);

      // grab updated object
      const race = await raceService.fetchRaceById(raceId);
      dispatch({ type: ActionTypes.SET_SELECTED_RACE_OBJ, payload: race });
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_RACES,
          error,
        },
      });
    }
  };
};

/**
 * @description action creator for getting all unapproved races
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const getAllUnapprovedRaces = (successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      const races = await raceService.fetchAllUnapprovedFutureRaces();
      dispatch({ type: ActionTypes.SET_ALL_UNAPPROVED_RACES, payload: races });
      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_UNAPPROVED_RACES,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for getting all approved races
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const getAllApprovedRaces = (successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      const races = await raceService.fetchAllApprovedFutureRaces();
      dispatch({ type: ActionTypes.SET_ALL_APPROVED_RACES, payload: races });
      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_APPROVED_RACES,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for admin approving a race
 * @param {String} raceId id of race
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const adminApproveRace = (raceId, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      await raceService.adminApproveRace(raceId);

      const unApprovedRaces = await raceService.fetchAllUnapprovedFutureRaces();
      dispatch({ type: ActionTypes.SET_ALL_UNAPPROVED_RACES, payload: unApprovedRaces });

      const approvedRaces = await raceService.fetchAllApprovedFutureRaces();
      dispatch({ type: ActionTypes.SET_ALL_APPROVED_RACES, payload: approvedRaces });

      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_UNAPPROVED_RACES,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for admin unapproving a race
 * @param {String} raceId id of race
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const adminUnapproveRace = (raceId, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      await raceService.adminUnapproveRace(raceId);

      const unApprovedRaces = await raceService.fetchAllUnapprovedFutureRaces();
      dispatch({ type: ActionTypes.SET_ALL_UNAPPROVED_RACES, payload: unApprovedRaces });

      const approvedRaces = await raceService.fetchAllApprovedFutureRaces();
      dispatch({ type: ActionTypes.SET_ALL_APPROVED_RACES, payload: approvedRaces });

      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_UNAPPROVED_RACES,
          error,
        },
      });

      failureCallback(error);
    }
  };
};
